/**
 * Anlegen und Kopieren einer Kampagne — der eine Schreibweg.
 *
 * Der Chat öffnet seit Stufe 1 denselben Dialog wie die Kampagnenseite, statt dessen
 * Fragen als Chat-Karten nachzubauen. Damit das etwas wert ist, darf auch der Schreibweg
 * nur einmal existieren: sonst driften zwei Fassungen auseinander, und genau das ist
 * Ticket `e9a194e2` — dort prüften Chat und Formular verschiedene Felder, der Chat meldete
 * „vollständig", während der Weiter-Button gesperrt blieb.
 *
 * Der zweite Aufruf (`updateFunnel`) ist kein Schönheitsfehler, sondern nötig: `POST
 * /campaigns` nimmt das Konto als `linkedInAccountId` entgegen, hängt es aber nicht
 * überall dorthin, wo die Oberfläche es später liest. Beim Klonen gibt es diesen
 * Parameter gar nicht — die Kopie erbt das Konto der Vorlage und muss umgehängt werden.
 */

import { cloneFunnel, createFunnel, updateFunnel } from 'API/linkedin-funnel';
import { Funnel, FunnelTypes } from 'features/co-pilot/co-pilot.types';

import { isCampaignNameTakenError } from './campaign-name-taken';

export interface CreateCampaignInput {
  name: string;
  /** Recruiting → Active Sourcing, Sales → Social Selling. */
  campaignType: 'recruiting' | 'sales';
  linkedinAccountId: string;
  /** Gesetzt heißt: kopieren statt neu anlegen (Mindmap 2.3). */
  cloneFromCampaignId?: string;
  /** Kandidaten der Vorlage übernehmen (Mindmap 2.3.1). */
  cloneContacts?: boolean;
}

export interface CreateCampaignResult {
  campaign?: Funnel;
  /** Der Name ist im Tenant schon vergeben — die einzige Fehlermeldung mit eigenem Feld. */
  nameTaken?: boolean;
  error?: any;
}

export function funnelTypeFor(campaignType: 'recruiting' | 'sales'): FunnelTypes {
  return campaignType === 'sales' ? FunnelTypes.SOCIAL_SELLING_CO_PILOT : FunnelTypes.ACTIVE_SOURCING_CO_PILOT;
}

export async function createOrCloneCampaign(input: CreateCampaignInput): Promise<CreateCampaignResult> {
  const { name, campaignType, linkedinAccountId, cloneFromCampaignId, cloneContacts } = input;

  const res = cloneFromCampaignId
    ? await cloneFunnel(cloneFromCampaignId, name, !!cloneContacts)
    : await createFunnel({
        name,
        type: funnelTypeFor(campaignType) as FunnelTypes.ACTIVE_SOURCING_CO_PILOT | FunnelTypes.SOCIAL_SELLING_CO_PILOT,
        linkedinAccountId,
        funnelContacts: [],
        settings: {
          language: 'de',
          secondaryLanguage: 'en',
        },
      });

  if (res?.error) {
    return {
      error: res.error,
      nameTaken: isCampaignNameTakenError(res.error),
    };
  }

  const resUpdate = await updateFunnel(res._id, { linkedinAccount: linkedinAccountId });
  return { campaign: (resUpdate?.error ? res : resUpdate) as Funnel };
}
